import axios from 'axios';
import * as uuid from 'uuid';
import { formatResponseResults } from '../helpers/extractedResults';

/**
 * Send Audio to Amazon's S3 cloud storage
 * @param {Blob} blob - User's audio recording
 * @param {*} metaData - Object which holds the scenerio presented to the user in the form {                
 *              userID: string,
                audience: string,
                environment: string,
                script: string
            } 
 */
export async function sendS3(blob: Blob, metaData: any) {
    let id: string = uuid.v4();
    let fileType = 'audio/wav';

    try {
        // Get the s3 location on where to put the audio file
        const response: any = await axios.post('/api/presigned-url', {
            activity: 'teacherVoice',
            fileType: fileType,
            metaData: metaData,
        });
        var signedPostRequest = response.data;
        var fields = signedPostRequest.fields;

        var options = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        };

        // Put audio file into s3 bucket
        var bodyFormData = new FormData();
        for (var key in fields) {
            bodyFormData.append(key, fields[key]);
        }

        bodyFormData.append('file', blob);
        await axios.post(signedPostRequest.url, bodyFormData, options);

        if (response.data.fields?.key) {
            return response.data.fields?.key;
        } else {
            throw new Error(`Audio Failed to update: Missing Audio ID`);
        }
    } catch (error) {
        throw new Error(`Audio Failed to update: ${error}`);
    }
}

/**
 * Sends user's questionnaire answers to database
 * @param {*} data - User's audio recording. see userAnswers.ts formatUserData function for format
 */
export async function sendUserData(data: any) {
    try {
        const response = await axios.post('/api/teacherVoice/user', data, {
            headers: {
                'content-type': 'application/json',
            },
        });

        if (response.status !== 200) {
            throw new Error(response.statusText);
        }
    } catch (error) {
        throw new Error(`Failed to upload User Data: ${error}`);
    }
}

/**
 * Gets audio processing results
 * @param {string} audioID - User's recording ID, which is received when recording is sent to storage
 */
export async function pollResults(audioID: string) {
    try {
        const response = await axios.get<any>('/api/features', {
            params: {
                activity: 'teacherVoice',
                audioID: audioID,
            },
        });

        // incase anything besides a 200 doesn't thrown an error
        if (response.status !== 200) {
            throw new Error(response.statusText);
        }
        return response.data?.Item
            ? formatResponseResults(response.data?.Item)
            : null;
    } catch (error) {
        // 404 will be caught here and we don't want it to throw an error
        if (axios.isAxiosError(error)) {
            if (error?.response?.status === 404) return null;
        }

        throw new Error(`Failed to upload User Data: ${error}`);
    }
}

/**
 * Gets audio processing results
 * @param {string} audioID - User's recording ID, which is received when recording is sent to storage
 */
export async function ping() {
    try {
        const response = await axios.get<any>('/api/ping');

        // incase anything besides a 200 doesn't thrown an error
        if (response.status !== 200) {
            throw new Error(response.statusText);
        }
    } catch (error) {
        throw new Error(`Server is down`);
    }
}

export async function computeCanadaUploadAudio(blob: Blob, fileName: string) {
    const URL = 'https://206-12-100-41.cloud.computecanada.ca:9000/api/upload';
    let fileType = 'audio/wav';

    try {
        var options = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        };

        // Put audio file into s3 bucket
        var bodyFormData = new FormData();
        bodyFormData.append('fields', fileName);
        bodyFormData.append('files', blob);
        await axios.post(URL, bodyFormData, options);

        return fileName;
    } catch (error) {
        // we don't throw an error
    }
}

export async function statistics() {
    const URL = '/api/teacherVoice/metrics';
    try {
        const response = await axios.get<any>(URL);

        // incase anything besides a 200 doesn't thrown an error
        if (response.status !== 200) {
            throw new Error(response.statusText);
        }

        return response?.data;
    } catch (error) {
        throw new Error(`Server is down`);
    }
}
